import { Paper, Typography, Grid, Box, Stack } from '@mui/material';
import { Masonry } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';

function extractHexColors(text: string): string[] {
    // Matches #RRGGBB or #RGB
    return Array.from(text.matchAll(/#(?:[0-9a-fA-F]{3}){1,2}/g), (m) => m[0]);
}

export type BrandingProps = {
    name: string;
    username: string;
    category: string;
    writingLanguage: string;
    marketSegment: string;
    targetAudience: string;
    toneOfVoice: string;
    writingStyle: string;
    objectives: string;
    keywords: string[];
    hashtags: string[];
    visualIdentity: {
        colorPalette: string;
        typography: string;
        layout: string;
        visualElements: string;
        extraDetails: string;
    };
};

const MarkdownCaption = ({ children }: { children: string }) => (
    <Typography
        variant="caption"
        component="div"
        sx={{
            '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: 'inherit', fontWeight: 600, mt: 1, mb: 0.5 },
            '& p': { m: 0 },
            '& ul, & ol': { m: 0, pl: 2 },
            '& li': { mb: 0.25 },
        }}
    >
        <Markdown>{children}</Markdown>
    </Typography>
);

export const Branding = (props: BrandingProps) => {
    const {
        name,
        username,
        category,
        writingLanguage,
        marketSegment,
        targetAudience,
        toneOfVoice,
        writingStyle,
        objectives,
        keywords,
        hashtags,
        visualIdentity,
    } = props;
    const { t } = useTranslation();

    const colorHexes = extractHexColors(visualIdentity.colorPalette);

    return (
        <Stack spacing={1} sx={{ width: 1 }}>
            <Masonry columns={{ xs: 1, md: 2 }} spacing={2} sx={{ mt: 2, width: 1 }}>
                {/* Brand Identity */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <Typography variant="h6">
                                {t('onboarding.chat.branding.brand-identity')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.name')}:
                                </Typography>
                                <Typography variant="caption">{name}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.username')}:
                                </Typography>
                                <Typography variant="caption">@{username}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.category')}:
                                </Typography>
                                <Typography variant="caption">{category}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.tone-of-voice')}:
                                </Typography>
                                <MarkdownCaption>{toneOfVoice}</MarkdownCaption>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.writing-style')}:
                                </Typography>
                                <MarkdownCaption>{writingStyle}</MarkdownCaption>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
                {/* Target Audience */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <Typography variant="h6">
                                {t('onboarding.chat.branding.target-audience')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.target-audience.primary')}:
                                </Typography>
                                <MarkdownCaption>{targetAudience}</MarkdownCaption>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.writing-language')}:
                                </Typography>
                                <MarkdownCaption>{writingLanguage}</MarkdownCaption>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.market-segment')}:
                                </Typography>
                                <MarkdownCaption>{marketSegment}</MarkdownCaption>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
                {/* Visual Identity */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <Typography variant="h6">
                                {t('onboarding.chat.branding.visual-identity')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.visual-identity.color-palette')}:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    {colorHexes
                                        .sort((a, b) => a.localeCompare(b))
                                        .map((hex, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: hex,
                                                    borderRadius: 1,
                                                    border: '1px solid #ddd',
                                                }}
                                                title={hex}
                                            />
                                        ))}
                                </Box>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.visual-identity.typography')}:
                                </Typography>
                                <MarkdownCaption>{visualIdentity.typography}</MarkdownCaption>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.visual-identity.layout')}:
                                </Typography>
                                <MarkdownCaption>{visualIdentity.layout}</MarkdownCaption>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.visual-identity.visual-elements')}:
                                </Typography>
                                <MarkdownCaption>{visualIdentity.visualElements}</MarkdownCaption>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.visual-identity.extra-details')}:
                                </Typography>
                                <MarkdownCaption>{visualIdentity.extraDetails}</MarkdownCaption>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
                {/* Content Strategy */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <Typography variant="h6">
                                {t('onboarding.chat.branding.content-strategy')}
                            </Typography>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.keywords')}:
                                </Typography>
                                <Typography variant="caption">{keywords.join(', ')}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.hashtags')}:
                                </Typography>
                                <Typography variant="caption">{hashtags.join(', ')}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('onboarding.chat.branding.objectives')}:
                                </Typography>
                                <MarkdownCaption>{objectives}</MarkdownCaption>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
            </Masonry>
        </Stack>
    );
};
